/**
 * Re-reading the credits after a phone is verified, so the daily-limit wall that offered the
 * verification clears on the raised ceiling (`dailyLimit.verifyPhoneLimit`).
 *
 * One read is not enough: the BFF may still answer the old ceiling for a moment after the recipient
 * gains its phone. So it reads at 0s, 2s and 5s, and stops early as soon as the answer has moved —
 * today's `limit` changed, or the offer field disappeared. Same policy as iOS and Android.
 *
 * Import-free on purpose, so the policy is unit-tested without the app's graph (and without the shared
 * query client, which `useCodeVerification`'s tests mock away).
 */

/** The credits read's query key — must equal `queryKeys.creditsBalance` (pinned by a test). Duplicated
 *  here because importing `query-client` constructs the client, which the verification tests cannot. */
export const CREDITS_BALANCE_QUERY_KEY = ['credits-balance'] as const;

/** When to re-read, measured from the verification's success. */
export const PHONE_VERIFIED_CREDITS_RETRY_DELAYS_MS = [0, 2_000, 5_000] as const;

/** The two numbers the stop rule compares. */
export interface DailyCeilingSnapshot {
  limit: number;
  verifyPhoneLimit: number | null;
}

/** The snapshot out of a cached `/api/credits/balance` payload, or null when it carries no daily ceiling. */
export function dailyCeilingSnapshot(data: unknown): DailyCeilingSnapshot | null {
  const daily = (data as { account?: { dailyLimit?: { limit?: unknown; verifyPhoneLimit?: unknown } | null } | null } | null | undefined)
    ?.account?.dailyLimit;
  if (!daily || typeof daily.limit !== 'number') return null;
  return { limit: daily.limit, verifyPhoneLimit: typeof daily.verifyPhoneLimit === 'number' ? daily.verifyPhoneLimit : null };
}

/**
 * Has the read moved since the verification? Yes when there was nothing to compare against (no offer
 * was on screen, so one read is all it takes), when the ceiling is gone or changed, or when the offer
 * field disappeared.
 */
export function creditsSettledAfterPhone(before: DailyCeilingSnapshot | null, after: DailyCeilingSnapshot | null): boolean {
  if (!before) return true;
  if (!after) return true;
  return after.limit !== before.limit || after.verifyPhoneLimit === null;
}

/**
 * Run the policy: snapshot, then refetch on each delay until the read has settled. A failed refetch is
 * just another try — the wall staying up one more read is the cheap error.
 */
export async function refreshCreditsAfterPhoneVerified(deps: {
  read: () => DailyCeilingSnapshot | null;
  refetch: () => Promise<unknown>;
  sleep?: (ms: number) => Promise<void>;
}): Promise<void> {
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const before = deps.read();
  let waited = 0;
  for (const at of PHONE_VERIFIED_CREDITS_RETRY_DELAYS_MS) {
    if (at > waited) await sleep(at - waited);
    waited = at;
    try {
      await deps.refetch();
    } catch {
      // try again on the next delay
    }
    if (creditsSettledAfterPhone(before, deps.read())) return;
  }
}
